// Edit Request types for the web dashboard

import { displayPhDate, displayPhTime } from '@/lib/phDate';

export interface EditRequest {
  id: string;
  farm_id: string;
  target_table: 'water_readings' | 'feedings' | 'samplings' | 'harvests' | 'applications' | 'water_checks';
  target_id: string;
  requested_by: string;
  requested_by_name: string;
  requested_at: string;
  reason: string | null;
  original_data: Record<string, unknown>;
  proposed_data: Record<string, unknown>;
  status: 'pending' | 'approved' | 'rejected';
  resolved_by: string | null;
  resolved_by_name: string | null;
  resolved_at: string | null;
  rejection_reason: string | null;
}

export interface EditRequestListResponse {
  data: {
    requests: EditRequest[];
    has_more: boolean;
  };
  meta: {
    timestamp: string;
  };
}

export interface EditRequestCountSummary {
  pending: number;
  approved: number;
  rejected: number;
}

export interface EditRequestCountResponse {
  data: EditRequestCountSummary;
  meta: {
    timestamp: string;
  };
}

export interface EditHistory {
  id: string;
  target_table: string;
  target_id: string;
  changed_by: string;
  changed_by_name: string;
  changed_at: string;
  change_type: 'direct' | 'approved_request';
  previous_data: Record<string, unknown>;
  new_data: Record<string, unknown>;
  reason: string | null;
}

export const TABLE_DISPLAY_NAMES: Record<string, string> = {
  water_readings: 'Water reading',
  feedings: 'Feeding',
  samplings: 'Sampling',
  harvests: 'Harvest',
  applications: 'Application',
  water_checks: 'Water check',
};

export function formatTableName(table: string): string {
  return TABLE_DISPLAY_NAMES[table] || table;
}

const TARGET_DATE: Record<string, { field: string; label: string }> = {
  water_readings: { field: 'reading_date', label: 'Reading date' },
  feedings: { field: 'feeding_date', label: 'Feeding date' },
  samplings: { field: 'sampling_date', label: 'Sampling date' },
  harvests: { field: 'harvest_date', label: 'Harvest date' },
  applications: { field: 'application_date', label: 'Application date' },
  water_checks: { field: 'measured_at', label: 'Checked at' },
};

const TARGET_SLOT_FIELD: Record<string, string> = {
  water_readings: 'reading_slot',
  feedings: 'feeding_slot',
};

export interface TargetDetail {
  dateLabel: string;
  date: string | null;
  slot: string | null;
  product: string | null;
  productCategory: string | null;
  unit: string | null;
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

export function getTargetDetail(request: EditRequest): TargetDetail {
  const dateSpec = TARGET_DATE[request.target_table];
  const rawDate = dateSpec ? request.original_data[dateSpec.field] : null;
  let date: string | null = null;
  if (typeof rawDate === 'string' && rawDate) {
    date = rawDate.length === 10
      ? displayPhDate(rawDate)
      : `${displayPhDate(new Date(rawDate))}, ${displayPhTime(rawDate)}`;
  }
  const slotField = TARGET_SLOT_FIELD[request.target_table];
  const rawSlot = slotField ? request.original_data[slotField] : null;
  const isApplication = request.target_table === 'applications';
  return {
    dateLabel: dateSpec?.label ?? 'Date',
    date,
    slot: nonEmptyString(rawSlot),
    product: isApplication ? nonEmptyString(request.original_data.product_name) : null,
    productCategory: isApplication ? nonEmptyString(request.original_data.product_category) : null,
    unit: isApplication ? nonEmptyString(request.original_data.unit) : null,
  };
}

const FIELD_UNITS: Record<string, string> = { kg: 'kg', g: 'g', ml: 'mL', l: 'L', ppm: 'ppm', ppt: 'ppt', cm: 'cm', m: 'm', c: '°C' };

/** "feed_amount_kg" -> "Feed amount (kg)"; "dissolved_oxygen" -> "Dissolved oxygen". */
export function formatFieldName(field: string): string {
  const parts = field.split('_');
  const last = parts[parts.length - 1].toLowerCase();
  const unit = parts.length > 1 ? FIELD_UNITS[last] : undefined;
  const words = (unit ? parts.slice(0, -1) : parts).join(' ');
  const sentence = words.charAt(0).toUpperCase() + words.slice(1);
  return unit ? `${sentence} (${unit})` : sentence;
}
